<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  onClose: {
    type: Function,
    default: () => {},
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const alert = useAlert();

const title = ref('');
const description = ref('');
const priority = ref('1');
const attachments = ref([]);

const priorityOptions = computed(() => [
  { label: t('SUPPORT_TICKETS.PRIORITY.LOW'), value: '0' },
  { label: t('SUPPORT_TICKETS.PRIORITY.MEDIUM'), value: '1' },
  { label: t('SUPPORT_TICKETS.PRIORITY.HIGH'), value: '2' },
  { label: t('SUPPORT_TICKETS.PRIORITY.URGENT'), value: '3' },
]);

const isSubmitting = ref(false);

const handleFileChange = event => {
  const files = Array.from(event.target.files);
  files.forEach(file => {
    if (file.size > 5 * 1024 * 1024) {
      useAlert(t('SUPPORT_TICKETS.ERROR.FILE_TOO_LARGE'));
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      attachments.value.push({
        filename: file.name,
        content_type: file.type,
        size: file.size,
        // 使用完整的 base64 数据
        data: e.target.result.split(',')[1], // 移除 data:image/xxx; 前缀
        preview: URL.createObjectURL(file),
      });
    };
    reader.readAsDataURL(file);
  });
};

const removeAttachment = index => {
  URL.revokeObjectURL(attachments.value[index].preview);
  attachments.value.splice(index, 1);
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;

  if (!title.value.trim()) {
    useAlert(t('SUPPORT_TICKETS.ERROR.TITLE_REQUIRED'));
    return;
  }

  const ticketData = {
    title: title.value,
    description: description.value,
    content: description.value,
    priority: parseInt(priority.value, 10),
    attachments: attachments.value,
  };

  try {
    isSubmitting.value = true;
    await emit('submit', ticketData);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="w-full max-w-2xl">
    <!-- 头部 -->
    <div
      class="flex items-center justify-between px-8 py-6 border-b border-n-weak"
    >
      <div>
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.CREATE_NEW') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('SUPPORT_TICKETS.SUBTITLE') }}
        </p>
      </div>
      <Button variant="ghost" icon="i-lucide-x" size="sm" @click="onClose" />
    </div>

    <!-- 表单内容 -->
    <div class="px-8 py-6 space-y-6">
      <!-- 标题 -->
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.LABELS.TITLE') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <Input
          v-model="title"
          :placeholder="t('SUPPORT_TICKETS.PLACEHOLDERS.TITLE')"
        />
      </div>

      <!-- 优先级 -->
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.LABELS.PRIORITY') }}
        </label>
        <select
          v-model="priority"
          class="w-full px-4 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        >
          <option
            v-for="option in priorityOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- 描述 -->
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.LABELS.DESCRIPTION') }}
        </label>
        <TextArea
          v-model="description"
          :placeholder="t('SUPPORT_TICKETS.PLACEHOLDERS.DESCRIPTION')"
          :rows="6"
        />
      </div>

      <!-- 附件 -->
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.LABELS.ATTACHMENTS') }}
        </label>

        <label
          class="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer border-n-weak hover:border-n-brand bg-n-slate-1 hover:bg-n-brand-1 transition-colors"
        >
          <span class="text-n-slate-10">
            <span class="icon-upload-line text-lg" />
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('SUPPORT_TICKETS.UPLOAD_BUTTON') }}
          </span>
          <input
            type="file"
            class="hidden"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            @change="handleFileChange"
          />
        </label>

        <!-- 附件预览 -->
        <div v-if="attachments.length > 0" class="flex flex-wrap gap-3 mt-4">
          <div
            v-for="(attachment, index) in attachments"
            :key="index"
            class="relative group"
          >
            <img
              :src="attachment.preview"
              class="w-16 h-16 object-cover rounded-lg border border-n-weak"
              alt="预览"
            />
            <button
              class="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-n-ruby-11 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              @click="removeAttachment(index)"
            >
              <span class="icon-close-line text-xs" />
            </button>
          </div>
        </div>

        <p class="mt-2 text-xs text-n-slate-10">
          {{ t('SUPPORT_TICKETS.UPLOAD_HINT') }}
        </p>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div
      class="flex items-center justify-end gap-3 px-8 py-6 border-t border-n-weak bg-n-slate-1"
    >
      <Button
        variant="outline"
        :label="t('SUPPORT_TICKETS.BUTTONS.CANCEL')"
        @click="onClose"
      />
      <Button
        :label="t('SUPPORT_TICKETS.BUTTONS.SUBMIT')"
        :loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
